import Icon from '@ant-design/icons/lib/components/Icon';
import styled from '@emotion/styled';
import {
  Button,
  Checkbox,
  Dropdown,
  Menu,
  Modal,
  Popconfirm,
  Popover,
  Space,
  TableColumnType,
  Tag,
  Tooltip,
} from 'antd';
import { useAuthorization } from 'components/Authorization';
import ScrollTable, { presetRestHeight, ScrollTableProps } from 'components/ScrollTable';
import { getPermission, useRouteAccessProps } from 'hooks/business/useNewPermisson';
import useIsOU from 'hooks/business/useIsOU';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { useNavigate } from 'react-router-dom';
import { isMlj } from 'services/chattt';
import { ReactComponent as EllipsisIcon } from 'views/Aide/assets/more.svg';
import { What } from 'views/Marketing/components/PolicyTable';
import { translateFlatDepartmentAndRole } from '../utils';
import AccountStatus from './AccountStatus';
import { DownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { isMasterOnline, isSiteGiant } from 'productLine';
import { uniq } from 'lodash-es';

export const Confirm = styled.div`
  width: 120px;
`;
const DepartmentTag = styled(Tag)`
  display: flex;
  gap: 4px;
  color: var(--ant-primary-color);
  border-color: #d6e8ff;
  max-width: 100%;
  span {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;
const DepartmentGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  .account-department-ellipsis {
    cursor: pointer;
  }
`;

type RV = StoreHomeV2.Account;

interface OperationProps {
  account: RV;
  isExpire: boolean;
  isLimit: boolean;
  isTrail: boolean;
  onEditNick: (record: RV) => void;
  onEditPassword: (record: RV) => void;
  onUpdateStatus: (record: RV) => Promise<any>;
  onDelete: (record: RV) => Promise<any>;
}

function Operation(props: OperationProps) {
  const {
    account,
    isExpire,
    isLimit,
    isTrail,
    onEditNick,
    onEditPassword,
    onUpdateStatus,
    onDelete,
  } = props;

  const { status, id } = account;

  const nav = useNavigate();

  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { id: tokenId } = payload || {};

  const isSelf = `${id}` === `${tokenId}`;

  const { newPermissionEnable, newPermissionMap } = useRouteAccessProps();

  const [statusLoading, setStatusLoading] = useState(false);

  const [deleteLoading, setDeleteLoading] = useState(false);

  const { t } = useTranslate();

  const handleDelete = useCallback(() => {
    const { destroy } = Modal.confirm({
      title: t('account.deleteConfirm'),
      onOk: () => {
        destroy();

        setDeleteLoading(true);

        onDelete(account).finally(() => {
          setDeleteLoading(false);
        });
      },
    });
  }, [onDelete, account, t]);

  const editButton = (
    <Button
      type="link"
      // @target { ...account }     防止table与form 的对象引用，如有需求可以深拷贝
      onClick={useCallback(() => onEditNick({ ...account }), [onEditNick, account])}
    >
      {t('account.edit')}
    </Button>
  );
  const toggleDisableButton = !isSelf ? (
    status === 'DISABLE' && (isExpire || isLimit) ? (
      isMasterOnline ? null : (
        <Popconfirm
          title={
            <Confirm>
              {/* eslint-disable-next-line no-nested-ternary */}
              {isExpire
                ? isTrail
                  ? t('account.当前试用已到期，请先订购服务套餐')
                  : t('account.当前订购已到期，请先订购服务套餐')
                : t('account.当前坐席已达上限，请先订购客服坐席')}
            </Confirm>
          }
          okText={isSiteGiant ? null : t('account.去订购')}
          onConfirm={() => nav('/management/service/market')}
        >
          <Button type="text">
            {
              {
                ENABLE: t('account.disable'),
                DISABLE: t('account.enable'),
              }[status]
            }
          </Button>
        </Popconfirm>
      )
    ) : (
      <Button
        type="text"
        loading={statusLoading}
        onClick={() => {
          const action = () => {
            setStatusLoading(true);

            onUpdateStatus({
              ...account,
              status: (
                {
                  ENABLE: 'DISABLE',
                  DISABLE: 'ENABLE',
                } as const
              )[status],
            }).finally(() => {
              setStatusLoading(false);
            });
          };

          if (status === 'DISABLE') {
            action();
            return;
          }

          const { destroy } = Modal.confirm({
            title: t('account.disableConfirm'),
            onOk: () => {
              action();
              destroy();
            },
          });
        }}
      >
        {
          {
            ENABLE: t('account.disable'),
            DISABLE: t('account.enable'),
          }[status]
        }
      </Button>
    )
  ) : null;
  const updatePwdButton = isMlj ? null : (
    <Button type="text" onClick={() => onEditPassword(account)}>
      {t('account.updatePassword')}
    </Button>
  );
  const deleteButton = getPermission({
    newPermissionEnable,
    newPermissionMap,
    permissionName: '帐号管理',
  })
    ? status === 'DISABLE' && (
        <Button type="text" loading={deleteLoading} onClick={handleDelete}>
          {t('account.delete')}
        </Button>
      )
    : null;
  const items = [updatePwdButton, toggleDisableButton, deleteButton].filter(
    Boolean,
  ) as JSX.Element[];

  return (
    <div>
      {editButton}
      {isMasterOnline ? null : (
        <Dropdown
          arrow
          overlay={
            <Menu>
              {items.map((item, i) => (
                <Menu.Item key={String(i)}>{item}</Menu.Item>
              ))}
            </Menu>
          }
        >
          <Button type="link">
            {t('review.更多')} <DownOutlined />
          </Button>
        </Dropdown>
      )}
    </div>
  );
}

interface DepartmentGroupProps {
  data: Assistant.RoleInDepartment[];
  wrapRow?: number;
}

function DepartmentGroup(props: DepartmentGroupProps) {
  const { data = [], wrapRow = 2 } = props;
  const { t } = useTranslate();

  const ellipsisText = useMemo(
    () => (
      <Popover
        placement="bottom"
        content={data.slice(wrapRow, data.length).map((v) => {
          const dpn = v.departmentName === '客服部' ? t('department.客服部') : v.departmentName;
          return (
            <DepartmentTag
              key={`${v.departmentId}_${v.roleId}`}
              title={`${dpn}|${v.roleName}`}
              className="mb-[8px]"
            >
              <span>{dpn}</span>
              <span>|</span>
              <span>{v.roleName}</span>
            </DepartmentTag>
          );
        })}
      >
        <Space className="account-department-ellipsis">
          <span>{t('department.更多x部门', { x: data.length - wrapRow })}</span>
          <Icon component={EllipsisIcon} />
        </Space>
      </Popover>
    ),
    [data, wrapRow, t],
  );

  return (
    <DepartmentGroupContainer>
      {data.slice(0, wrapRow).map((v) => {
        const dpn = v.departmentName === '客服部' ? t('department.客服部') : v.departmentName;
        return (
          <DepartmentTag key={`${v.departmentId}_${v.roleId}`} title={`${dpn}|${v.roleName}`}>
            <span>{dpn}</span>
            <span>|</span>
            <span>{v.roleName}</span>
          </DepartmentTag>
        );
      })}
      {data.length > wrapRow && ellipsisText}
    </DepartmentGroupContainer>
  );
}

export const useAccountTypeLabel = () => {
  const { t } = useTranslate();

  const accountTypeLabel = {
    1: t('account.不限额'),
    2: t('account.限额'),
  };

  return {
    t,
    accountTypeLabel,
  };
};

export interface AccountTableProps extends ScrollTableProps<RV>, Omit<OperationProps, 'account'> {
  supportNonReceptionAccount?: boolean;
  nonReceptionProcessMonthlyLimit?: number;
  selectedIds: number[];
  onSelectedIdsChange: (selectedIds: number[]) => void;
}

function AccountTable(props: AccountTableProps) {
  const {
    pagination,
    isExpire,
    isLimit,
    isTrail,
    supportNonReceptionAccount = false,
    nonReceptionProcessMonthlyLimit = 300,
    dataSource,
    selectedIds,
    onSelectedIdsChange,
    onEditNick,
    onEditPassword,
    onUpdateStatus,
    onDelete,
    ...otherProps
  } = props;

  const { t, accountTypeLabel } = useAccountTypeLabel();

  const isOU = useIsOU();

  const columns = useMemo<TableColumnType<RV>[]>(() => {
    const { current = 1, pageSize = 10 } = pagination || {};

    const checked = (record: RV) => selectedIds.includes(record.id);
    const allChecked = dataSource?.every(checked);
    const indeterminate = !allChecked && dataSource?.some(checked);

    return [
      {
        key: 'select',
        title: (
          <Checkbox
            checked={allChecked}
            indeterminate={indeterminate}
            onChange={({ target: { checked } }) =>
              onSelectedIdsChange(
                checked
                  ? uniq([...selectedIds, ...(dataSource?.map(({ id }) => id) || [])])
                  : selectedIds.filter((id) => dataSource?.every((_) => _.id !== id)),
              )
            }
          />
        ),
        fixed: 'left',
        render: (_, record) => (
          <Checkbox
            checked={checked(record)}
            onChange={({ target: { checked } }) =>
              onSelectedIdsChange(
                checked
                  ? uniq([...selectedIds, record.id])
                  : selectedIds.filter((id) => id !== record.id),
              )
            }
          />
        ),
      },
      {
        key: 'index',
        title: t('field.index'),
        fixed: 'left',
        render: (_, __, index) => {
          return pageSize * (current - 1) + index + 1;
        },
      },
      ...(isOU
        ? [
            {
              key: 'id',
              dataIndex: 'id',
              title: 'ID',
              width: 80,
            },
          ]
        : []),
      {
        key: 'account',
        dataIndex: 'account',
        title: t('field.account'),
      },
      {
        key: 'name',
        dataIndex: 'name',
        title: t('field.name'),
      },
      {
        dataIndex: 'status',
        title: t('review.状态'),
        render: (value: RV['status'], { sleepFlag }: RV) => {
          return <AccountStatus status={value} sleepFlag={sleepFlag} />;
        },
      },
      {
        key: 'roleInDepartments',
        dataIndex: 'roleInDepartments',
        title: `${t('department.部门')} | ${t('department.角色')}`,
        render: (_: StoreHomeV2.Account['roleInDepartments'], record: StoreHomeV2.Account) => {
          if (record.roleInDepartments.length === 0) {
            return (
              <Tooltip title={t('department.noDepartmentDesc')}>
                <Tag color="error">
                  <span>{t('department.noDepartment')}</span>
                  <InfoCircleOutlined />
                </Tag>
              </Tooltip>
            );
          }
          return (
            <DepartmentGroup data={translateFlatDepartmentAndRole(record.roleInDepartments, t)} />
          );
        },
      },
      ...(isMasterOnline
        ? []
        : [
            {
              key: 'email',
              dataIndex: 'email',
              title: t('field.email'),
              width: 160,
              render: (email: RV['email']) => email || '-',
            },
            {
              key: 'phoneNumber',
              dataIndex: 'phoneNumber',
              title: t('field.phone'),
              width: 160,
              render: (phoneNumber: RV['phoneNumber']) => phoneNumber || '-',
            },
          ]),
      ...(supportNonReceptionAccount
        ? [
            {
              key: 'type',
              dataIndex: 'type',
              title: (
                <div>
                  {t('account.接待限额')}
                  <Tooltip
                    title={t('account.限额tips', { x: nonReceptionProcessMonthlyLimit })}
                    overlayInnerStyle={{ whiteSpace: 'pre-wrap' }}
                  >
                    <What />
                  </Tooltip>
                </div>
              ),
              render: (type: RV['type'], { isSuperAdmin }: RV) =>
                isSuperAdmin ? '-' : accountTypeLabel[type],
            },
          ]
        : []),
      {
        key: 'operation',
        title: t('操作'),
        fixed: 'right',
        render: (_, record: RV) => {
          return (
            <Operation
              account={record}
              isExpire={isExpire}
              isLimit={isLimit}
              isTrail={isTrail}
              onEditNick={onEditNick}
              onEditPassword={onEditPassword}
              onUpdateStatus={onUpdateStatus}
              onDelete={onDelete}
            />
          );
        },
      },
    ];
  }, [
    pagination,
    isExpire,
    isLimit,
    isTrail,
    onEditNick,
    onEditPassword,
    onUpdateStatus,
    onDelete,
    t,
    accountTypeLabel,
    supportNonReceptionAccount,
    nonReceptionProcessMonthlyLimit,
    isOU,
    dataSource,
    selectedIds,
    onSelectedIdsChange,
  ]);

  return (
    <ScrollTable
      rowKey="id"
      restHeight={presetRestHeight.withPagination}
      dataSource={dataSource}
      columns={columns}
      pagination={pagination}
      scroll={{ x: 'max-content' }}
      className="whitespace-nowrap"
      {...otherProps}
    />
  );
}

export default memo(AccountTable);
